'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'Too many findings, no clear priority',
    description:
      'Vulnerability scanners, pen test reports, and compliance gap analyses have produced a backlog of hundreds of findings. Your team does not know where to start. We cut through it, mapping findings to the controls that matter most to your auditor and your actual risk, and building a prioritised remediation programme.',
  },
  {
    tag: '[02]',
    title: 'Framework alignment without the overhead',
    description:
      'Multiple frameworks, overlapping requirements, and conflicting guidance create paralysis and wasted effort. We identify the controls that satisfy your auditor, reduce your real risk, and eliminate the work that does not move either needle, so your team focuses on what counts.',
  },
  {
    tag: '[03]',
    title: 'Post-assessment direction setting',
    description:
      'Following a security assessment, maturity review, or audit, organisations are left with a long list of recommendations and no clear sense of sequence. We translate the output into a prioritised programme with clear rationale for every decision, auditor-aligned and defensible.',
  },
  {
    tag: '[04]',
    title: 'Security budget and resource allocation',
    description:
      'Leadership needs to decide where to invest in security, tooling, headcount, programmes. We provide the analysis to make that decision based on actual risk exposure and framework requirements, not vendor marketing or benchmarks that do not reflect your environment.',
  },
];

const outcomes = [
  { metric: 'Auditor', label: 'Aligned to what your auditor actually needs' },
  { metric: 'Framework', label: 'Mapped to your target compliance framework' },
  { metric: 'Less noise', label: 'Unnecessary work identified and removed' },
  { metric: 'Justified', label: 'Every priority decision explained and defensible' },
];

const approach = [
  {
    step: 'Step 01',
    title: 'Understand your environment & framework scope',
    body: 'We start with your actual technology stack, your compliance target, and your current security controls. We map your environment to the framework requirements your auditor will test against, not a generic control list.',
  },
  {
    step: 'Step 02',
    title: 'Map your real threat exposure',
    body: 'We identify the threats that are actually relevant to your organisation, based on your sector, your technology choices, and your attack surface. Framework controls are then weighted by their relevance to your actual risk.',
  },
  {
    step: 'Step 03',
    title: 'Assess coverage and identify noise',
    body: 'We review what you have in place and assess how effectively each control reduces the risks that matter and satisfies your auditor. We also identify the work your team is doing that delivers neither, and remove it from the programme.',
  },
  {
    step: 'Step 04',
    title: 'Deliver a prioritised programme',
    body: 'A clear, justified programme of work, ordered by risk reduction impact and auditor relevance. Each item includes the rationale, the expected outcome, the framework control it satisfies, and the effort involved.',
  },
];

const differentiators = [
  {
    title: 'Auditor and framework aligned',
    body: 'We understand what auditors actually test and how frameworks are applied in practice. The programme we build is designed to satisfy your auditor, not just look good on paper.',
  },
  {
    title: 'Cut the unnecessary work',
    body: 'Most security programmes contain significant work that does not reduce risk or satisfy any auditor requirement. We identify it, remove it, and redirect that effort to what actually matters.',
  },
  {
    title: 'Decisions you can defend',
    body: 'Every priority recommendation comes with a clear rationale tied to your framework and your risk. When your board, your auditors, or your customers ask why you focused here and not there, you have a defensible answer.',
  },
];

export default function SecurityFocusPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '500px', height: '500px', bottom: '-80px', right: '3%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Security Focus Programme</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Security Focus Programme]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Auditor aligned.<br />
              <span className="text-[#80edfa]">No unnecessary work.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] mb-10 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              Security generates noise. Frameworks, findings, and guidance all compete for your team&apos;s attention, most of it disconnected from what your auditor actually tests or what genuinely reduces your risk. The Security Focus Programme cuts through it. We build a prioritised, framework-aligned programme that eliminates unnecessary work and focuses your team on the controls that satisfy auditors and close real exposure.
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up" style={{ transitionDelay: '0.2s' }}>
              <a href="#contact" className="btn-primary">Start the programme</a>
              <a href="#approach" className="btn-secondary">Our approach</a>
            </div>
          </div>
        </div>
      </section>

      {/* Callout strip */}
      <section className="border-t border-b border-[#80edfa]/20 py-8 bg-[#80edfa]/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-12">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-2 h-2 rounded-full bg-[#80edfa] animate-pulse" />
              <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Programme positioning</p>
            </div>
            <p className="text-sm text-[#aaa] leading-relaxed">
              The Security Focus Programme is not a generic prioritisation exercise. It is built around your specific framework target, your auditor&apos;s expectations, and your real threat exposure, so the work your team does is the work that counts.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-[#222] py-16 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="bg-[#050a0f] px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Where it helps</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                Situations where the programme delivers
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                The challenge is rarely a lack of security information, it is too much of it, with no clear signal about what your auditor needs or what actually reduces your risk. These are the situations where a focused, framework-aligned programme makes the biggest difference.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section id="approach" className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Our approach</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Framework-aligned. Auditor-ready. No wasted effort.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {approach?.map((a, i) => (
              <div key={i} className="bg-[#0a0f14] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest mb-4">{a?.step}</p>
                <div className="w-6 h-px bg-[#333] mb-6" />
                <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-3">{a?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{a?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Why TechSonar</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Prioritisation grounded in frameworks and reality
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-px bg-[#222]">
            {differentiators?.map((d, i) => (
              <div key={i} className="flex-1 bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-6" />
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{d?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{d?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Ready to cut the noise and focus on what counts?
            </h2>
            <p className="text-[#aaa] text-sm mt-2">Talk to a TechSonar engineer, we will build a programme aligned to your framework and your auditor.</p>
          </div>
          <div className="flex flex-wrap gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <a href="#contact" className="btn-primary">Start the programme</a>
            <Link href="/services" className="btn-secondary">View all services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
